import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "./AuthContext";
import { DEFAULT_STYLE } from "@/lib/tokens";

const DataContext = createContext(null);

// Custom-field tokens persisted per team (client-side; internal app)
const CUSTOM_FIELDS_KEY = "esm_custom_fields";

export function DataProvider({ children }) {
    const { user } = useAuth();
    const [templates, setTemplates] = useState([]);
    const [brands, setBrands] = useState([]);
    const [categories, setCategories] = useState([]);
    const [users, setUsers] = useState([]);
    const [emailStyles, setEmailStyles] = useState([]);
    const [customFields, setCustomFields] = useState([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        try {
            const raw = localStorage.getItem(CUSTOM_FIELDS_KEY);
            if (raw) setCustomFields(JSON.parse(raw));
        } catch (e) {
            // ignore
        }
    }, []);

    const persistCustomFields = (arr) => {
        setCustomFields(arr);
        localStorage.setItem(CUSTOM_FIELDS_KEY, JSON.stringify(arr));
    };

    const addCustomField = (name) => {
        const clean = name.trim();
        if (!clean) return;
        if (customFields.some((f) => f.toLowerCase() === clean.toLowerCase())) return;
        persistCustomFields([...customFields, clean]);
    };

    const reloadAll = useCallback(async () => {
        if (!user) return;
        // Resilient parallel load — one endpoint failing must not blank the
        // whole app (e.g. an old build where /email-styles doesn't exist).
        const settled = await Promise.allSettled([
            api.get("/templates"),
            api.get("/brands"),
            api.get("/categories"),
            api.get("/users"),
            api.get("/email-styles"),
        ]);
        const [t, b, c, u, s] = settled;
        setTemplates(t.status === "fulfilled" ? t.value.data : []);
        setBrands(b.status === "fulfilled" ? b.value.data : []);
        setCategories(c.status === "fulfilled" ? c.value.data : []);
        setUsers(u.status === "fulfilled" ? u.value.data : []);
        setEmailStyles(s.status === "fulfilled" ? s.value.data : []);
        setLoaded(true);
    }, [user]);

    const reloadTemplates = useCallback(async () => {
        const { data } = await api.get("/templates");
        setTemplates(data);
    }, []);
    const reloadBrands = useCallback(async () => {
        const { data } = await api.get("/brands");
        setBrands(data);
    }, []);
    const reloadCategories = useCallback(async () => {
        const { data } = await api.get("/categories");
        setCategories(data);
    }, []);
    const reloadUsers = useCallback(async () => {
        const { data } = await api.get("/users");
        setUsers(data);
    }, []);
    const reloadEmailStyles = useCallback(async () => {
        const { data } = await api.get("/email-styles");
        setEmailStyles(data);
    }, []);

    useEffect(() => {
        if (user) reloadAll();
        else setLoaded(false);
    }, [user, reloadAll]);

    const brandByKey = (key) => brands.find((b) => b.key === key);
    const styleById = (id) => (id ? emailStyles.find((s) => s.id === id) : null);
    // Effective style resolver — NEVER returns null. Guarantees callers
    // (previews, wrappers, editor labels) don't crash on a partially-
    // seeded DB. Falls back through: template.style_id → brand default →
    // first-available style → built-in DEFAULT_STYLE.
    const resolveStyleForTemplate = (template) => {
        if (template && template.style_id) {
            const found = styleById(template.style_id);
            if (found) return found;
        }
        if (template && template.brand_key) {
            const brand = brandByKey(template.brand_key);
            if (brand && brand.default_style_id) {
                const found = styleById(brand.default_style_id);
                if (found) return found;
            }
        }
        return emailStyles[0] || DEFAULT_STYLE;
    };

    return (
        <DataContext.Provider
            value={{
                templates,
                brands,
                categories,
                users,
                emailStyles,
                customFields,
                addCustomField,
                loaded,
                reloadAll,
                reloadTemplates,
                reloadBrands,
                reloadCategories,
                reloadUsers,
                reloadEmailStyles,
                brandByKey,
                styleById,
                resolveStyleForTemplate,
            }}
        >
            {children}
        </DataContext.Provider>
    );
}

export function useData() {
    const ctx = useContext(DataContext);
    if (!ctx) throw new Error("useData must be used inside DataProvider");
    return ctx;
}
